import { useMemo } from 'react';
import { useT } from '../i18n';
import { keyLabel, keymap, useKeys, type ActionId } from '../keys/store';

/* Reading-pane action bar. Shortcut hints come from the live keymap, so a
   rebound key is reflected here instead of the tooltip quietly lying. */

const ICONS = {
    archive: 'M3.5 6.5h17v3.5h-17zM5.5 10v9a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-9M10 14h4',
    move: 'M3 7.5a2 2 0 0 1 2-2h3.6l2 2.2H19a2 2 0 0 1 2 2v8.8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-11zM12 11v6M9.5 14.5L12 17l2.5-2.5',
    trash: 'M4 7h16M9.5 7V4.8h5V7M6.5 7l.9 13h9.2l.9-13',
    star: 'M12 3.6l2.6 5.4 5.9.85-4.25 4.15 1 5.9L12 17.1l-5.25 2.8 1-5.9L3.5 9.85l5.9-.85L12 3.6z',
    unread: 'M3 6.5h18v11H3zM3 7l9 6.5L21 7',
    reply: 'M9 10L4 15l5 5M4 15h11a5 5 0 0 0 0-10h-2',
    replyAll: 'M9 10L4 15l5 5M13 10L8 15l5 5M8 15h9a5 5 0 0 0 0-10h-2',
    forward: 'M15 10l5 5-5 5M20 15H9a5 5 0 0 1 0-10h2',
} as const;

function Button({
    icon,
    title,
    testId,
    onClick,
    active,
    disabled,
}: {
    icon: string;
    title: string;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    active?: boolean;
    disabled?: boolean;
    /* A handle that does not change with the reader's language. The title says "Sil (delete)" in
       Turkish and "Delete (delete)" in English, and a test that matches on either is a test that
       breaks the day somebody translates something. */
    testId?: string;
}) {
    return (
        <button
            data-testid={testId}
            data-hit
            title={title}
            onClick={onClick}
            disabled={disabled}
            style={{
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: '1px solid transparent',
                borderRadius: 'var(--r-btn)',
                color: disabled ? 'var(--text-mute)' : active ? 'var(--amber)' : 'var(--text-dim)',
                opacity: disabled ? 0.4 : 1,
            }}
        >
            <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill={active ? 'var(--amber)' : 'none'}
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d={icon} />
            </svg>
        </button>
    );
}

export default function Toolbar(props: {
    visible: boolean;
    starred: boolean;
    unread: boolean;
    canArchive: boolean;
    onArchive: () => void;
    /** Opens the folder picker anchored under the button that was clicked. */
    onMove: (anchor: { x: number; y: number }) => void;
    onTrash: () => void;
    onStar: () => void;
    onToggleSeen: () => void;
    onReply: () => void;
    onReplyAll: () => void;
    onForward: () => void;
}) {
    const { t } = useT();
    const preset = useKeys((s) => s.preset);
    const overrides = useKeys((s) => s.overrides);
    const map = useMemo(() => keymap(preset, overrides), [preset, overrides]);
    const hint = (id: ActionId) => keyLabel(map[id]).toLowerCase();
    const title = (label: string, id: ActionId) => `${label} (${hint(id)})`;

    return (
        <div
            style={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                /*
                    Kısayol ipucu şeridi kaldırıldı (arayüz v2, 3. adım): aynı listeyi `?` kartı
                    zaten tam ve okunur biçimde veriyordu, buradaki kopya ise dar panelde üç
                    noktayla kesiliyor — yani en çok ihtiyaç duyulduğu yerde en az işe yarıyordu.

                    Şerit gidince sabit yükseklik de gitti: düğmeler artık sarabiliyor. Eskiden
                    saramamalarının sebebi tam olarak o şeritti.
                */
                flexWrap: 'wrap',
                gap: 2,
                minHeight: 48,
                padding: '2px 12px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface)',
                visibility: props.visible ? 'visible' : 'hidden',
            }}
        >
            <Button icon={ICONS.reply} title={title(t('action.reply'), 'reply')} testId="toolbar-reply" onClick={props.onReply} />
            <Button icon={ICONS.replyAll} title={title(t('action.replyAll'), 'replyAll')} testId="toolbar-reply-all" onClick={props.onReplyAll} />
            <Button icon={ICONS.forward} title={title(t('action.forward'), 'forward')} testId="toolbar-forward" onClick={props.onForward} />
            <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 6px' }} />
            <Button icon={ICONS.archive} title={title(t('action.archive'), 'archive')} testId="toolbar-archive" onClick={props.onArchive} disabled={!props.canArchive} />
            <Button
                icon={ICONS.move}
                title={title(t('action.move'), 'move')}
                onClick={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    props.onMove({ x: r.left, y: r.bottom + 4 });
                }}
            />
            <Button icon={ICONS.trash} title={title(t('action.trash'), 'delete')} testId="toolbar-delete" onClick={props.onTrash} />
            <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 6px' }} />
            <Button icon={ICONS.star} title={title(t('action.star'), 'star')} onClick={props.onStar} active={props.starred} />
            <Button
                icon={ICONS.unread}
                title={props.unread ? t('action.markRead') : title(t('action.markUnread'), 'unread')}
                testId="toolbar-seen"
                onClick={props.onToggleSeen}
            />
        </div>
    );
}
